/**
 * The review block at the top of ISFR's Approval Summary, in the format
 * reviewers know from the Innovation Sandbox Reviews agent: Path, Billing and Cap
 * checks with a short note each; Technical Alignment; a Context section when
 * something needs flagging; and the design-win verdict with each design-win
 * criterion. Each check and criterion is marked ✅ (pass), ❌ (fail) or ❓ (could not be verified), with
 * a short justification. The same block copies as plain text for the APFP
 * internal comment, which has no length limit.
 *
 * Every mark comes from a result the agent recorded, never from a threshold
 * applied here:
 *   - Path and Billing from the good-standing check, which waits on data access.
 *   - Cap from the request-amount and annual-cap checks.
 *   - The design-win verdict from the design-win check. Each criterion from
 *     where the check found its supporting passage: a ✅ needs the passage in the
 *     plan (positive, submission-specific evidence); evidence the plan does not
 *     give, or that only the business description gives, is ❓, as in the review
 *     format, and the verdict line carries the check's own result.
 *   - Technical Alignment from the Financial category, with the funding
 *     formula's arithmetic.
 *   - The Context lines from the checks that recorded something to flag, and
 *     the routing note from the approvals check's result.
 * Check ids are internal keys here and never shown. Pure, so it is unit-tested.
 */
import { firstLine } from './reviewRecord';
import { formatValue } from './checkEvidence';

export const MARK = { PASS: 'pass', FAIL: 'fail', VERIFY: 'verify', NOT_APPLICABLE: 'not_applicable' };

export const MARK_EMOJI = {
  [MARK.PASS]: '✅', [MARK.FAIL]: '❌', [MARK.VERIFY]: '❓', [MARK.NOT_APPLICABLE]: '➖',
};

const money = (n) => (typeof n === 'number' ? formatValue(n, 'money').replace(/\.00$/, '') : '');

/**
 * The first sentence of a check's details; the category tab has them in full. A
 * leading result word ("FAIL. The total ...", "Defer - no diagram ...") only
 * repeats the status beside it, so the text after it is used.
 */
export const firstSentence = (text) => {
  const line = firstLine(text).replace(/^(PASS|FAIL|FLAG)[.:]\s*/, '').replace(/^Defer\s*[-–—:]\s*/i, '');
  const match = line.match(/^(.+?[.!?])(\s|$)/);
  const sentence = (match ? match[1] : line).trim();
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

const NOT_SAVED = 'Not saved for this request';

// A check's result as a mark, where the result settles it.
const markOf = (result) => ({
  PASS: MARK.PASS, FAIL: MARK.FAIL,
}[result] || MARK.VERIFY);

function goodStandingLine(label, check, what) {
  if (!check) return { label, mark: MARK.VERIFY, text: NOT_SAVED };
  if (check.result === 'DEFERRED' || check.result === 'UNVERIFIED') {
    return { label, mark: MARK.VERIFY, text: `Verify by hand on the Partner Funding Dashboard: ${what} data is not connected yet` };
  }
  return { label, mark: markOf(check.result), text: firstLine(check.details) };
}

function capLine(byId, partnerName) {
  const label = 'Cap OK?';
  const amount = byId.get('1.6');
  const annual = byId.get('1.2');
  if (!amount) return { label, mark: MARK.VERIFY, text: NOT_SAVED };
  const e = amount.evidence || {};
  if (amount.result === 'FAIL') return { label, mark: MARK.FAIL, text: firstLine(amount.details) };
  if (e.is_sca === true) {
    return { label, mark: MARK.PASS, text: `SCA partner${partnerName ? `: ${partnerName}` : ''}` };
  }
  const within = `${money(e.amount)} requested: within the ${money(e.cap)} cap`;
  if (annual && annual.result === 'PASS') return { label, mark: MARK.PASS, text: firstLine(annual.details) || within };
  if (annual && annual.result === 'FAIL') return { label, mark: MARK.FAIL, text: firstLine(annual.details) };
  return { label, mark: MARK.VERIFY, text: `${within}; the calendar-year total is yours to verify` };
}

// The design-win criteria in the order the review reads them, each with the
// review format's name for what it tests where the two line up.
const CRITERIA = [
  { key: 'broad_availability', label: 'Broad availability (repeatable solution)' },
  { key: 'novelty', label: 'Novelty (new product or significant architecture change)' },
  { key: 'partner_ip', label: 'Partner IP (partner-built components)' },
  { key: 'aws_integration', label: 'AWS integration' },
  { key: 'production_intent', label: 'Production intent (customer-facing offering)' },
];

// Where the check found a criterion's passage, as a mark and a short note.
const PASSAGE = {
  located: [MARK.PASS, 'found in the plan'],
  blank: [MARK.VERIFY, 'not described in the plan'],
  description_only: [MARK.VERIFY, 'only in the business description, not the plan'],
  description_unconfirmed: [MARK.VERIFY, 'in the business description; verify the plan shows it'],
  too_short: [MARK.VERIFY, 'passage too short to count; verify'],
  unlocated: [MARK.VERIFY, 'passage not found in the plan; verify'],
  absent: [MARK.VERIFY, 'no passage returned; verify'],
};

function designWinLines(check, notApplicable) {
  const label = 'Design Win';
  if (notApplicable) {
    return [{ label, mark: MARK.NOT_APPLICABLE, text: "Not applicable at this request's tier" }];
  }
  if (!check) return [{ label, mark: MARK.VERIFY, text: NOT_SAVED }];
  const e = check.evidence || {};
  const score = typeof e.overall_confidence === 'number' ? ` (${e.overall_confidence} / 100)` : '';
  // A pass reads as the check's assessment; anything else as the reason the
  // check gave for it, which its details lead with.
  let text = `${firstSentence(e.overall_assessment || check.details)}${score}`;
  if (check.result === 'DEFERRED') text = `Not completed: ${firstLine(check.details)}`;
  else if (check.result !== 'PASS') text = `${firstSentence(check.details)}${score}`;
  const verdict = { label, mark: markOf(check.result), text };
  const status = e.plan_quote_status && typeof e.plan_quote_status === 'object' ? e.plan_quote_status : {};
  const criteria = CRITERIA.filter((c) => e[c.key] && typeof e[c.key] === 'object').map((c) => {
    const raw = e[c.key];
    const [mark, where] = PASSAGE[status[c.key]] || [MARK.VERIFY, status[c.key] ? `${status[c.key]}; verify` : 'no passage status; verify'];
    const scoreText = typeof raw.score === 'number' ? ` (${raw.score} / 100)` : '';
    return {
      label: c.label,
      mark,
      text: `${firstSentence(raw.assessment)} Plan evidence: ${where}.${scoreText}`.trim(),
      indent: true,
    };
  });
  return [verdict, ...criteria];
}

function technicalAlignmentLine(financial, byId) {
  const label = 'Technical Alignment';
  if (!financial) return { label, mark: MARK.VERIFY, text: NOT_SAVED };
  const formula = (byId.get('2.1') || {}).evidence || {};
  const math = typeof formula.monthly_estimate === 'number' && typeof formula.allowed_ceiling === 'number'
    ? `${money(formula.monthly_estimate)}/mo supports up to ${money(formula.allowed_ceiling)} over ${formula.funded_days} days; ${money(formula.requested_amount)} requested`
    : '';
  const failing = financial.members.map((id) => byId.get(id)).find((c) => c && (c.result === 'FAIL' || c.result === 'ERROR'));
  const incomplete = financial.members.map((id) => byId.get(id)).find((c) => c && ['DEFERRED', 'UNVERIFIED', 'PARTIAL'].includes(c.result));
  let mark = MARK.VERIFY;
  if (financial.state === 'pass') mark = MARK.PASS;
  else if (financial.state === 'failed' && failing) mark = MARK.FAIL;
  const why = failing ? `${failing.name}: ${firstSentence(failing.details)}`
    : (incomplete && mark !== MARK.PASS ? `Not completed: ${incomplete.name}` : '');
  return { label, mark, text: [math, why].filter(Boolean).join('. ') || 'Cost documentation supports the build' };
}

// The distributor line the check block carries only when the submission shows
// the work is for another entity.
function distributorLine(check) {
  const e = (check && check.evidence) || {};
  if (!check || !['FAIL', 'FLAG'].includes(check.result) || e.on_behalf_of_downstream !== true) return null;
  return {
    label: 'Distributor',
    mark: MARK.FAIL,
    text: `Submitting on behalf of ${e.downstream_entity || 'another entity'}`,
  };
}

// Pre-screen checks the summary already shows elsewhere: the cap, the distributor
// line and the special program have lines of their own, and the documents check's
// inputs are the Submitted inputs line.
const OWN_LINE = new Set(['1.4', '1.6', '1.16', '1.19']);

const flagged = (check) => Boolean(check) && (check.result === 'FAIL' || check.result === 'FLAG');
const nameOf = (check) => check.name || 'Unnamed check';

// What the review format flags in its Context, as plain lines with no mark: a
// special program, the eligibility pre-screen items that failed or flagged,
// resubmission history once partner history is connected, checks whose results
// contradict each other, and checks that did not complete. An SCA partner is
// named on the Cap line, so it is not repeated here.
function contextLines(view, get, partnerName) {
  const lines = [];
  const add = (label, text) => lines.push({ label, mark: null, text });
  const program = get('1.19');
  const markers = program && Array.isArray(program.evidence.matched_markers)
    ? program.evidence.matched_markers.filter(Boolean) : [];
  if (markers.length > 0) add('Special program', `${markers.join(', ')}: it routes differently after your review`);
  const eligibility = view.rollup.categories.find((c) => c.key === 'eligibility');
  (eligibility ? eligibility.members : []).filter((id) => !OWN_LINE.has(id)).map(get).filter(flagged)
    .forEach((c) => add(`${nameOf(c)} (${c.result === 'FAIL' ? 'failed' : 'flagged'})`, firstSentence(c.details)));
  ['1.8', '5.11'].map(get).filter(flagged).forEach((c) => add(nameOf(c), firstSentence(c.details)));
  const cross = get('5.10');
  (cross && Array.isArray(cross.evidence.contradictions) ? cross.evidence.contradictions : [])
    .filter((t) => typeof t === 'string' && t.trim() !== '')
    .forEach((t) => add('Possible anomaly', t.trim()));
  const errored = view.incomplete.errored.value;
  if (errored.length > 0) add('Did not complete', errored.map(nameOf).join(', '));
  return lines;
}

/**
 * The routing note over the PSA review bar: the request goes on to a PSA after
 * the contractor's review. The approvals check flags exactly
 * the requests that need PSA (or executive) approval, so the note keys off its
 * recorded result, never an amount here.
 */
/** The approvals check, which records whether the request needs PSA or executive approval. */
export const approvalsCheck = (view) => (view.checks.status === 'ok' ? view.checks.byId.get('1.13') : undefined);

export function routingNote(view) {
  const approvals = approvalsCheck(view);
  if (!approvals || approvals.result !== 'FLAG') return null;
  return {
    label: 'Proceed to PSA',
    text: "After your review, reassign to the PSA. The PSA's review is the technical and financial review.",
  };
}

/**
 * The review format's role views. The PSA view expands, below the review block,
 * the review those roles own and hides nothing: the design-win evaluation, and the
 * technical review (financial validation, service alignment, sizing, and the
 * architecture). Category keys are the agent's taxonomy keys.
 */
export const ROLE_VIEWS = [
  { id: 'contractor', text: 'Contractor', categories: [] },
  { id: 'psa', text: 'PSA', categories: ['design_win', 'financial', 'architecture'] },
];

/**
 * The categories whose checks a role's Validations Performed counts: the PSA view
 * counts the review the PSA owns, the contractor view everything else.
 */
export function roleCountedCategories(view, roleId) {
  if (view.rollup.status !== 'ok') return [];
  const psa = ROLE_VIEWS.find((r) => r.id === 'psa').categories;
  return view.rollup.categories.filter((c) => (roleId === 'psa' ? psa.includes(c.key) : !psa.includes(c.key)));
}

/** The categories a role view expands, in the record's review order. */
export function roleCategories(view, roleId) {
  const role = ROLE_VIEWS.find((r) => r.id === roleId);
  if (!role || view.rollup.status !== 'ok') return [];
  return view.rollup.categories.filter((c) => role.categories.includes(c.key));
}

/**
 * The block's lines, in display order, each {label, mark, text, indent?}, in
 * groups: the checks, technical alignment, context (only when something needs
 * flagging), and the design win.
 */
export function buildReviewBlock(view, record) {
  const byId = view.checks.byId;
  const saved = view.checks.status === 'ok';
  const get = (id) => (saved ? byId.get(id) : undefined);
  const partnerName = (record && record.approval_summary && record.approval_summary.partner) || '';
  const designWin = view.rollup.categories.find((c) => c.members.includes('3.4') || c.notApplicable.includes('3.4'));
  const financial = view.rollup.categories.find((c) => c.key === 'financial');
  const context = saved ? contextLines(view, get, partnerName) : [];
  return [
    {
      key: 'checks',
      lines: [
        goodStandingLine('Path OK?', get('1.1'), 'partner path'),
        goodStandingLine('Billing OK?', get('1.1'), 'billing'),
        saved ? capLine(byId, partnerName) : { label: 'Cap OK?', mark: MARK.VERIFY, text: NOT_SAVED },
        distributorLine(get('1.16')),
      ].filter(Boolean),
    },
    {
      key: 'technical',
      lines: [saved ? technicalAlignmentLine(financial, byId) : { label: 'Technical Alignment', mark: MARK.VERIFY, text: NOT_SAVED }],
    },
    ...(context.length > 0 ? [{ key: 'context', lines: context }] : []),
    {
      key: 'design_win',
      lines: designWinLines(get('3.4'), Boolean(designWin && designWin.notApplicable.includes('3.4'))),
    },
  ];
}

/**
 * The block as plain text for the APFP internal comment, in the same order as
 * the view: the Recommendation with any routing note, the checks, Technical
 * Alignment, the Context when there is any, then the design win. "Path OK? ✅ (note)" for a check,
 * "Label: ✅ : justification" for the rest, "Label: text" for a context line,
 * one line each and a blank line between sections.
 */
export const lineText = (line) => {
  if (!line.mark) return `${line.label}: ${line.text}`;
  return line.label.endsWith('?')
    ? `${line.label} ${MARK_EMOJI[line.mark]}${line.text ? ` (${line.text})` : ''}`
    : `${line.indent ? '  ' : ''}${line.label}: ${MARK_EMOJI[line.mark]}${line.text ? ` : ${line.text}` : ''}`;
};

export function reviewBlockText(groups, verdictLabel, routing = null) {
  const recommendation = [
    verdictLabel && `Recommendation: ${verdictLabel}`,
    routing && `${routing.label}: ${routing.text}`,
  ].filter(Boolean);
  const sections = recommendation.length > 0 ? [recommendation] : [];
  groups.forEach((group) => sections.push(
    group.key === 'context' ? ['Context', ...group.lines.map(lineText)] : group.lines.map(lineText),
  ));
  return sections.map((section) => section.join('\n')).join('\n\n');
}
